import React, { useEffect } from "react";

/**
 * Shared modal used across admin pages.
 * Renders only when `open` is true; closes on backdrop click, ESC, or × button.
 * Keeps content scrollable on small viewports.
 */
export default function AdminDialog({
  open,
  onClose,
  title,
  subtitle,
  maxWidth = "max-w-xl",
  testid = "admin-dialog",
  footer = null,
  children,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-0 md:p-4"
      onClick={onClose}
      data-testid={`${testid}-backdrop`}
    >
      <div
        className={`bg-white w-full ${maxWidth} max-h-[92vh] md:max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testid}
      >
        <div className="p-6 border-b border-[hsl(var(--kb-border))] flex justify-between items-start gap-4 sticky top-0 bg-white z-10">
          <div className="min-w-0">
            {subtitle && <div className="overline">{subtitle}</div>}
            <h2 className="font-display text-2xl md:text-3xl truncate">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-2xl leading-none shrink-0 text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-text))]"
            aria-label="Close dialog"
            data-testid={`${testid}-close`}
          >
            ×
          </button>
        </div>
        <div className="p-6 space-y-4">{children}</div>
        {footer && (
          <div className="p-4 border-t border-[hsl(var(--kb-border))] flex gap-3 justify-end sticky bottom-0 bg-white">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
